import {
  SbSchemaConditionOperator,
  SbSchemaField,
  SbSchemaForm,
  SbSchemaOption,
  SbSchemaValidation,
} from './sb-schema-form.types';

/**
 * The server's settings validation (`registrationPlatformsApiIntegration/settings/validate.js`),
 * ported rule for rule so the panel refuses what a save would refuse, in the same words.
 *
 * Messages are fixed sentences built from the form's own labels and limits. They never quote a
 * submitted value: a settings form carries credentials.
 */

type Values = Record<string, unknown>;

const isPlainObject = (value: unknown): value is Values => value !== null && typeof value === 'object'
  && !Array.isArray(value) && [Object.prototype, null].includes(Object.getPrototypeOf(value));

export const isBlank = (value: unknown): boolean => value === undefined || value === null
  || (typeof value === 'string' && value.trim() === '')
  || (Array.isArray(value) && value.length === 0)
  || (isPlainObject(value) && Object.keys(value).length === 0);

/**
 * The `conditions` operators (the server's settings/conditions.js): the ids and labels of the
 * template display conditions, minus the numeric ones. `isSet` and `isEmpty` take no value.
 */
export const SB_SCHEMA_CONDITION_OPERATORS: SbSchemaConditionOperator[] = [
  { id: 'equals', label: 'is', needsValue: true },
  { id: 'notEquals', label: 'is not', needsValue: true },
  { id: 'contains', label: 'contains', needsValue: true },
  { id: 'notContains', label: 'does not contain', needsValue: true },
  { id: 'isSet', label: 'has a value', needsValue: false },
  { id: 'isEmpty', label: 'is empty', needsValue: false },
];

export const SB_SCHEMA_CONDITION_MATCHES = ['all', 'any'];
export const SB_SCHEMA_CONDITION_MAX_RULES = 20;
export const SB_SCHEMA_CONDITION_MAX_VALUE_LENGTH = 500;
export const SB_SCHEMA_CONDITION_MAX_FIELD_LENGTH = 200;

const operatorOf = (id: unknown): SbSchemaConditionOperator | null =>
  SB_SCHEMA_CONDITION_OPERATORS.find((operator) => operator.id === id) || null;

/**
 * The operators a `conditions` field allows, in its order: the form's `operators` (the server
 * sends `{id, label, needsValue}`; bare ids are taken too), else all of them. Unknown ids are left out.
 */
export const conditionOperators = (field: SbSchemaField): SbSchemaConditionOperator[] => {
  if (!Array.isArray(field.operators) || !field.operators.length) {
    return SB_SCHEMA_CONDITION_OPERATORS;
  }
  return field.operators
    .map((operator) => {
      const id = typeof operator === 'object' && operator ? operator.id : operator;
      const known = operatorOf(id);
      const label = typeof operator === 'object' && operator && operator.label ? operator.label : known?.label;
      return known ? { ...known, label } : null;
    })
    .filter((operator): operator is SbSchemaConditionOperator => !!operator);
};

/** Whether a rule with this operator needs a value; an unknown or missing operator does. */
export const conditionNeedsValue = (operator: unknown): boolean => {
  const known = operatorOf(operator);
  return known ? known.needsValue : true;
};

const clone = <T>(value: T): T => (value === undefined ? value : JSON.parse(JSON.stringify(value)));

const TRUE_STRINGS = ['true', '1', 'yes', 'on'];
const FALSE_STRINGS = ['false', '0', 'no', 'off', ''];

export const toBoolean = (value: unknown): unknown => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  if (typeof value === 'string') {
    const text = value.trim().toLowerCase();
    if (TRUE_STRINGS.includes(text)) {
      return true;
    }
    if (FALSE_STRINGS.includes(text)) {
      return false;
    }
  }
  return value;
};

const toNumberOrSame = (value: unknown): unknown => {
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return value;
};

export const isVisible = (field: SbSchemaField, values: Values | null | undefined): boolean => {
  if (!field.visibleWhen) {
    return true;
  }
  const actual = values ? values[field.visibleWhen.field] : undefined;
  const expected = field.visibleWhen.equals;
  // An unset switch is off.
  if (typeof expected === 'boolean') {
    return (toBoolean(actual) === true) === expected;
  }
  return actual === expected || String(actual) === String(expected);
};

const trimmedText = (value: unknown): unknown => {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return value;
};

/** One rule trimmed; the value is left out where the operator takes none. */
const coerceRule = (rule: Values): Values => {
  const result: Values = {};
  if (rule.field !== undefined) {
    result.field = trimmedText(rule.field);
  }
  if (rule.operator !== undefined) {
    result.operator = trimmedText(rule.operator);
  }
  const operator = operatorOf(result.operator);
  if (rule.value !== undefined && rule.value !== null && !(operator && !operator.needsValue)) {
    result.value = trimmedText(rule.value);
  }
  return result;
};

const isBlankRule = (rule: Values): boolean => isBlank(rule.field) && isBlank(rule.operator) && isBlank(rule.value);

/**
 * A `conditions` value as a save sends it: each rule trimmed, a rule blank everywhere dropped, the
 * value dropped where the operator takes none, the match mode `all` unless set. No rule left is
 * null, so a cleared filter is stored as no filter.
 */
const coerceConditions = (value: unknown): unknown => {
  if (typeof value === 'string' && value.trim() === '') {
    return null;
  }
  if (!isPlainObject(value) || !Array.isArray(value.rules)) {
    return value;
  }
  const rules = value.rules
    .filter((rule) => isPlainObject(rule))
    .map((rule: Values) => coerceRule(rule))
    .filter((rule) => !isBlankRule(rule));
  if (!rules.length) {
    return null;
  }
  const match = typeof value.match === 'string' ? value.match.trim() : value.match;
  return { match: isBlank(match) ? 'all' : match, rules };
};

/** Where each rule `prepareValues` keeps came from: prepared rule N is `rules[result[N]]`. */
export const keptRuleIndexes = (rules: unknown): number[] => {
  if (!Array.isArray(rules)) {
    return [];
  }
  const kept: number[] = [];
  rules.forEach((rule, index) => {
    if (isPlainObject(rule) && !isBlankRule(coerceRule(rule))) {
      kept.push(index);
    }
  });
  return kept;
};

const coerce = (field: SbSchemaField, value: unknown): unknown => {
  if (value === undefined || value === null) {
    return value;
  }
  switch (field.type) {
    case 'secret':
      return typeof value === 'string' ? value.trim() : value;
    case 'text':
    case 'url':
    case 'textarea':
    case 'json':
    case 'select':
    case 'radio':
      // Cleared is null, not '': the sync drops nulls, while a plugin's Joi.string() refuses ''.
      if (typeof value !== 'string') {
        return value;
      }
      return value.trim() === '' ? null : value.trim();
    case 'number':
      if (typeof value === 'string' && value.trim() === '') {
        return null;
      }
      return toNumberOrSame(value);
    case 'boolean':
      return toBoolean(value);
    case 'segment':
      if (typeof value === 'string' && value.trim() === '') {
        return null;
      }
      return toNumberOrSame(value);
    case 'rows': {
      if (!Array.isArray(value)) {
        return value;
      }
      const subFields = field.fields || [];
      return value
        .filter((row) => isPlainObject(row))
        .map((row: Values) => {
          const result: Values = { ...row };
          for (const subField of subFields) {
            if (result[subField.key] !== undefined) {
              result[subField.key] = coerce(subField, result[subField.key]);
            }
          }
          return result;
        })
        .filter((row) => subFields.some((subField) => !isBlank(row[subField.key])));
    }
    case 'conditions':
      return coerceConditions(value);
    case 'mapping': {
      let pairs: unknown = value;
      if (Array.isArray(value)) {
        const collected: Values = {};
        for (const item of value) {
          if (item && typeof item.key === 'string') {
            collected[item.key] = item.value;
          }
        }
        pairs = collected;
      }
      if (!isPlainObject(pairs)) {
        return value;
      }
      const result: Values = {};
      for (const [key, mapped] of Object.entries(pairs)) {
        const text = typeof mapped === 'string' ? mapped.trim() : mapped;
        if (!isBlank(text)) {
          result[key] = text;
        }
      }
      return result;
    }
    default:
      return value;
  }
};

/**
 * Applies defaults and coerces each form field's value to its type. Keys the form does not
 * declare pass through untouched. Secrets keep '' as "leave the stored value as it is"; any other
 * blank text becomes null.
 */
export const prepareValues = (form: SbSchemaForm, values: unknown): Values => {
  const result: Values = isPlainObject(values) ? clone(values) : {};
  for (const field of form.fields || []) {
    if (field.type === 'note' || field.type === 'copy') {
      continue;
    }
    if (result[field.key] === undefined && field.defaultValue !== undefined && field.type !== 'secret') {
      result[field.key] = clone(field.defaultValue);
    }
    if (result[field.key] !== undefined) {
      result[field.key] = coerce(field, result[field.key]);
    }
  }
  return result;
};

/**
 * Where each row `prepareValues` keeps came from: prepared row N is `rows[result[N]]`. Blank rows
 * are dropped before validation, so the server's `rows.N.sub` keys count kept rows only.
 */
export const keptRowIndexes = (field: SbSchemaField, rows: unknown): number[] => {
  if (!Array.isArray(rows)) {
    return [];
  }
  const kept: number[] = [];
  rows.forEach((row, index) => {
    if ((coerce(field, [row]) as unknown[]).length) {
      kept.push(index);
    }
  });
  return kept;
};

const isValidUrl = (value: unknown): boolean => {
  try {
    const parsed = new URL(String(value));
    return ['http:', 'https:'].includes(parsed.protocol) && Boolean(parsed.hostname);
  } catch {
    return false;
  }
};

const rangeMessage = (field: SbSchemaField): string => {
  if (field.min !== undefined && field.max !== undefined) {
    return `${field.label} must be between ${field.min} and ${field.max}`;
  }
  if (field.min !== undefined) {
    return `${field.label} must be at least ${field.min}`;
  }
  return `${field.label} must be at most ${field.max}`;
};

/** The type-specific checks for one non-blank value. Returns the message, or null. */
const checkValue = (field: SbSchemaField, value: unknown): string | null => {
  switch (field.type) {
    case 'text':
    case 'secret':
    case 'textarea':
      if (typeof value === 'object') {
        return `${field.label} must be text`;
      }
      break;
    case 'number': {
      const number = Number(value);
      if (!['number', 'string'].includes(typeof value) || !Number.isFinite(number)) {
        return `${field.label} must be a number`;
      }
      if ((field.min !== undefined && number < field.min) || (field.max !== undefined && number > field.max)) {
        return rangeMessage(field);
      }
      break;
    }
    case 'url':
      if (!isValidUrl(value)) {
        return `${field.label} must be a valid URL starting with http:// or https://`;
      }
      break;
    case 'json': {
      let parsed: unknown;
      try {
        parsed = typeof value === 'string' ? JSON.parse(value) : value;
      } catch {
        return `${field.label} must be valid JSON`;
      }
      if (field.shape === 'object' && !isPlainObject(parsed)) {
        return `${field.label} must be a JSON object`;
      }
      break;
    }
    case 'radio':
    case 'select':
      if (!(field.options || []).some((option) => String(option.value) === String(value))) {
        return `${field.label}: choose one of the listed options`;
      }
      break;
    case 'segment': {
      const number = Number(value);
      if (!Number.isInteger(number) || number <= 0) {
        return `${field.label}: choose a segment`;
      }
      break;
    }
    case 'boolean':
      if (typeof toBoolean(value) !== 'boolean') {
        return `${field.label} must be on or off`;
      }
      break;
    default:
      break;
  }
  if (field.pattern !== undefined && typeof value !== 'object' && !new RegExp(field.pattern).test(String(value))) {
    return field.patternMessage || `${field.label} has an invalid format`;
  }
  return null;
};

const checkRows = (field: SbSchemaField, rows: unknown, errors: Record<string, string>): void => {
  if (!Array.isArray(rows)) {
    errors[field.key] = `${field.label} must be a list`;
    return;
  }
  const minItems = field.minItems || 0;
  if (rows.length < minItems) {
    errors[field.key] = minItems === 1
      ? `${field.label} needs at least one complete row`
      : `${field.label} needs at least ${minItems} complete rows`;
  }
  rows.forEach((row, index) => {
    for (const subField of field.fields || []) {
      const value = row ? row[subField.key] : undefined;
      const path = `${field.key}.${index}.${subField.key}`;
      if (isBlank(value)) {
        if (subField.required) {
          errors[path] = `${subField.label} is required`;
        }
        continue;
      }
      const message = checkValue(subField, value);
      if (message) {
        errors[path] = message;
      }
    }
  });
};

const optionLabel = (options: SbSchemaOption[] | undefined, value: string): string => {
  const option = (options || []).find((item) => String(item.value) === String(value));
  return option ? option.label : String(value);
};

const checkMapping = (field: SbSchemaField, mapping: unknown, errors: Record<string, string>): void => {
  if (!isPlainObject(mapping)) {
    errors[field.key] = `${field.label} must be a field-to-field mapping`;
    return;
  }
  const known = new Set((field.keyOptions || []).map((option) => String(option.value)));
  if (Object.keys(mapping).some((key) => !known.has(key))) {
    errors[field.key] = `${field.label} contains a field that cannot be mapped`;
    return;
  }
  if (Object.values(mapping).some((mapped) => !['string', 'number'].includes(typeof mapped))) {
    errors[field.key] = `${field.label}: map each field to a platform field name`;
    return;
  }
  const missing = (field.requiredKeys || []).filter((key) => isBlank(mapping[key]));
  if (missing.length) {
    errors[field.key] = `${field.label}: ${missing.map((key) => optionLabel(field.keyOptions, key)).join(' and ')} must be mapped`;
  }
};

const checkConditions = (field: SbSchemaField, conditions: unknown, errors: Record<string, string>): void => {
  if (!isPlainObject(conditions) || !Array.isArray(conditions.rules)) {
    errors[field.key] = `${field.label} must be a list of filter rules`;
    return;
  }
  if (conditions.match !== undefined && !SB_SCHEMA_CONDITION_MATCHES.includes(conditions.match as string)) {
    errors[field.key] = `${field.label}: choose whether all rules or any rule must match`;
    return;
  }
  const maxRules = field.maxRules || SB_SCHEMA_CONDITION_MAX_RULES;
  if (conditions.rules.length > maxRules) {
    errors[field.key] = `${field.label} can hold at most ${maxRules} rule${maxRules === 1 ? '' : 's'}`;
    return;
  }
  // fieldOptions are suggestions: a rule may name any record field unless the form says strictFields
  const fieldOptions = field.fieldOptions && field.strictFields === true
    ? new Set(field.fieldOptions.map((option) => option.value)) : null;
  const operators = new Set(conditionOperators(field).map((operator) => operator.id));
  conditions.rules.forEach((rule, index) => {
    const path = `${field.key}.${index}`;
    const data: Values = isPlainObject(rule) ? rule : {};
    if (isBlank(data.field)) {
      errors[`${path}.field`] = 'Field is required';
    } else if (typeof data.field !== 'string') {
      errors[`${path}.field`] = 'Field must be text';
    } else if (data.field.length > SB_SCHEMA_CONDITION_MAX_FIELD_LENGTH) {
      errors[`${path}.field`] = `Field must be at most ${SB_SCHEMA_CONDITION_MAX_FIELD_LENGTH} characters`;
    } else if (fieldOptions && !fieldOptions.has(data.field)) {
      errors[`${path}.field`] = 'Field: choose one of the listed fields';
    }
    const operator = typeof data.operator === 'string' && operators.has(data.operator) ? operatorOf(data.operator) : null;
    if (isBlank(data.operator)) {
      errors[`${path}.operator`] = 'Operator is required';
    } else if (!operator) {
      errors[`${path}.operator`] = 'Operator: choose one of the listed operators';
    }
    if (!operator || !operator.needsValue) {
      return;
    }
    if (isBlank(data.value)) {
      errors[`${path}.value`] = 'Value is required';
    } else if (typeof data.value !== 'string') {
      errors[`${path}.value`] = 'Value must be text';
    } else if (data.value.length > SB_SCHEMA_CONDITION_MAX_VALUE_LENGTH) {
      errors[`${path}.value`] = `Value must be at most ${SB_SCHEMA_CONDITION_MAX_VALUE_LENGTH} characters`;
    }
  });
};

/** Filter rules without a rule are no filter at all. */
const hasNoRules = (field: SbSchemaField, value: unknown): boolean => field.type === 'conditions' && isPlainObject(value)
  && Array.isArray(value.rules) && !value.rules.length;

/**
 * @param form - the server's form description
 * @param values - prepared values (prepareValues)
 * @param context - secret keys that already hold a stored value
 */
export const validateValues = (
  form: SbSchemaForm,
  values: unknown,
  context: { configuredSecrets?: string[] } = {},
): SbSchemaValidation => {
  const configured = new Set(context.configuredSecrets || []);
  const data: Values = isPlainObject(values) ? values : {};
  const errors: Record<string, string> = {};
  const formErrors: string[] = [];
  const fieldsByKey = new Map((form.fields || []).map((field) => [field.key, field]));
  const present = (key: string): boolean => {
    const field = fieldsByKey.get(key);
    if (field && field.type === 'secret' && configured.has(key) && isBlank(data[key])) {
      return true;
    }
    return !isBlank(data[key]);
  };

  for (const field of form.fields || []) {
    if (['hidden', 'note', 'copy'].includes(field.type)) {
      continue;
    }
    if (!isVisible(field, data)) {
      continue;
    }
    const value = data[field.key];
    if (!present(field.key) || hasNoRules(field, value)) {
      // An empty list or mapping still owes its minimum rows and required keys.
      if (field.type === 'rows' && (field.minItems || 0) > 0) {
        checkRows(field, [], errors);
      } else if (field.type === 'mapping' && (field.requiredKeys || []).length) {
        checkMapping(field, {}, errors);
      } else if (field.required) {
        errors[field.key] = `${field.label} is required`;
      }
      continue;
    }
    if (isBlank(value)) {
      continue; // a configured secret left blank keeps its stored value
    }
    if (field.type === 'rows') {
      checkRows(field, value, errors);
    } else if (field.type === 'mapping') {
      checkMapping(field, value, errors);
    } else if (field.type === 'conditions') {
      checkConditions(field, value, errors);
    } else {
      const message = checkValue(field, value);
      if (message) {
        errors[field.key] = message;
      }
    }
  }

  // For a rule, a switch counts only when it is on, and a hidden field not at all.
  const filled = (key: string): boolean => {
    const field = fieldsByKey.get(key);
    if (field && !isVisible(field, data)) {
      return false;
    }
    if (field && field.type === 'boolean') {
      return toBoolean(data[key]) === true;
    }
    return present(key);
  };
  for (const rule of form.rules || []) {
    const visible = rule.fields.filter((key) => {
      const field = fieldsByKey.get(key);
      return field && isVisible(field, data);
    });
    if (rule.type === 'atLeastOne') {
      if (visible.length && !visible.some(filled)) {
        formErrors.push(rule.message);
      }
    } else if (rule.type === 'requiredWith' && filled(rule.when)) {
      for (const key of visible) {
        if (!present(key) && !errors[key]) {
          errors[key] = rule.message;
        }
      }
    }
  }

  return { valid: !Object.keys(errors).length && !formErrors.length, errors, formErrors };
};
